import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';

import { App } from 'antd';

import { handleServiceError } from 'lib/helpers/ServiceHelper';
import { GenerateIssuesIndicator, GenerateMaintenancesIndicator, GetSchedule } from 'services/contracts/Dashboard.contract';
import { generateIssuesIndicator, generateMaintenancesIndicator, getSchedule } from 'services/Dashboard.service';

const DashboardContext = createContext<Value | null>(null);

type Props = {
    children: (value: Value) => ReactNode
};

type Filters = {
    start_date: string | null,
    end_date: string | null,
    client_id: number | null
};

type Value = {
    isLoading: boolean,
    issuesIndicator: Dashboard.IssuesIndicator,
    setIssuesIndicator: Dispatch<SetStateAction<Value['issuesIndicator']>>,
    maintenancesIndicator: Dashboard.MaintenancesIndicator,
    setMaintenancesIndicator: Dispatch<SetStateAction<Value['maintenancesIndicator']>>,
    schedule: Dashboard.Schedule,
    setSchedule: Dispatch<SetStateAction<Value['schedule']>>
    filters: Filters
    setFilters: Dispatch<SetStateAction<Value['filters']>>
};

export function DashboardContextProvider({ children }: Props) {
    const [isLoading, setIsLoading] = useState(true);
    const [issuesIndicator, setIssuesIndicator] = useState<Dashboard.IssuesIndicator>({} as Dashboard.IssuesIndicator);
    const [maintenancesIndicator, setMaintenancesIndicator] = useState<Dashboard.MaintenancesIndicator>({} as Dashboard.MaintenancesIndicator);
    const [schedule, setSchedule] = useState<Dashboard.Schedule>([]);
    const [filters, setFilters] = useState<Filters>({
        start_date: null,
        end_date: null,
        client_id: null
    });

    const app = App.useApp();

    const fetchIssuesIndicator = useCallback(async (body: GenerateIssuesIndicator.Body = filters) => {
        setIsLoading(true);

        const response = await generateIssuesIndicator(body);

        setIsLoading(false);

        if (!response.success)
            return handleServiceError(app, response);


        setIssuesIndicator(response.indicators);
    }, [app, filters]);

    const fetchMaintenancesIndicator = useCallback(async (body: GenerateMaintenancesIndicator.Body = filters) => {
        setIsLoading(true);

        const response = await generateMaintenancesIndicator(body);

        setIsLoading(false);

        if (!response.success)
            return handleServiceError(app, response);

        setMaintenancesIndicator(response.indicators);
    }, [app, filters]);

    const fetchSchedule = useCallback(async (body: GetSchedule.Body = filters) => {
        setIsLoading(true);

        const response = await getSchedule(body);

        setIsLoading(false);

        if (!response.success)
            return handleServiceError(app, response);

        setSchedule(response.schedule);
    }, [app, filters]);

    useEffect(() => {
        fetchIssuesIndicator(filters);
        fetchMaintenancesIndicator(filters);
        fetchSchedule(filters);
    }, [fetchIssuesIndicator, fetchMaintenancesIndicator, fetchSchedule, filters]);


    const value: Value = {
        isLoading,
        issuesIndicator,
        setIssuesIndicator,
        maintenancesIndicator,
        setMaintenancesIndicator,
        schedule,
        setSchedule,
        filters,
        setFilters
    };

    return (
        <DashboardContext.Provider value={value}>
            {children(value)}
        </DashboardContext.Provider>
    );
}

export function useDashboard() {
    const context = useContext(DashboardContext);

    if (!context)
        throw new Error('Context is unknown. Perhaps the hook invocation is not inside a `DashboardContextProvider`.');

    return context;
}